import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import {
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  DollarSign,
  Users,
  HardDrive,
  Zap,
  Puzzle,
  MessageSquare,
  RefreshCw,
  Shield,
  BarChart3,
  FolderOpen,
  Star
} from 'lucide-react';
import toast from 'react-hot-toast';

const iconMap = {
  Puzzle, MessageSquare, RefreshCw, DollarSign, Zap, Shield, BarChart3, FolderOpen
};

const categoryLabels = {
  integration: 'Integration',
  module: 'Module',
  feature: 'Feature'
};

const categoryColors = {
  integration: 'bg-blue-100 text-blue-700',
  module: 'bg-purple-100 text-purple-700',
  feature: 'bg-green-100 text-green-700'
};

const PlansPage = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingAddOn, setEditingAddOn] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    priceMonthly: '',
    priceYearly: '',
    pricePerUserMonthly: '',
    pricePerUserYearly: '',
    minUsers: '',
    maxUsers: '',
    maxClients: '',
    maxStorageGB: '',
    maxEmailAccounts: '',
    aiCredits: '',
    features: [],
    isActive: true,
    isFeatured: false
  });
  const [addOnForm, setAddOnForm] = useState({
    name: '',
    slug: '',
    description: '',
    category: 'module',
    priceMonthly: '',
    priceYearly: '',
    pricePerUserMonthly: '',
    pricePerUserYearly: '',
    isPerUser: false,
    transactionFeePercent: '',
    icon: 'Puzzle',
    features: '',
    isActive: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (isRetryAfterSeed = false) => {
    setLoading(true);
    try {
      // Fetch plans and add-ons independently so one failure doesn't kill both
      const [plansResult, addOnsResult] = await Promise.allSettled([
        adminApi.getPlans(),
        adminApi.getAddOns()
      ]);

      const loadedPlans = plansResult.status === 'fulfilled' ? (plansResult.value.data.data || []) : [];
      const loadedAddOns = addOnsResult.status === 'fulfilled' ? (addOnsResult.value.data.data || []) : [];

      if (plansResult.status === 'rejected') console.error('Failed to load plans:', plansResult.reason);
      if (addOnsResult.status === 'rejected') console.error('Failed to load add-ons:', addOnsResult.reason);

      // Auto-seed default plans/add-ons if database is empty (first run)
      if (loadedPlans.length === 0 && loadedAddOns.length === 0 && !isRetryAfterSeed) {
        try {
          await adminApi.seedDefaults();
          toast.success('Default plans and add-ons created');
          return fetchData(true);
        } catch (seedErr) {
          console.error('Auto-seed failed:', seedErr);
        }
      }

      setPlans(loadedPlans);
      setAddOns(loadedAddOns);
    } catch (err) {
      toast.error('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Plan Handlers ---
  const handleOpenPlanModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        description: plan.description || '',
        priceMonthly: plan.priceMonthly,
        priceYearly: plan.priceYearly,
        pricePerUserMonthly: plan.pricePerUserMonthly || '',
        pricePerUserYearly: plan.pricePerUserYearly || '',
        minUsers: plan.minUsers || '',
        maxUsers: plan.limits?.maxUsers || '',
        maxClients: plan.limits?.maxClients || '',
        maxStorageGB: plan.limits?.maxStorageGB || '',
        maxEmailAccounts: plan.limits?.maxEmailAccounts || '',
        aiCredits: plan.limits?.aiCredits || '',
        features: plan.features || [],
        isActive: plan.isActive,
        isFeatured: plan.isFeatured || false
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: '',
        description: '',
        priceMonthly: '',
        priceYearly: '',
        pricePerUserMonthly: '',
        pricePerUserYearly: '',
        minUsers: '5',
        maxUsers: '',
        maxClients: '',
        maxStorageGB: '',
        maxEmailAccounts: '',
        aiCredits: '',
        features: [],
        isActive: true,
        isFeatured: false
      });
    }
    setShowPlanModal(true);
  };

  const handleSubmitPlan = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...planForm,
        priceMonthly: parseFloat(planForm.priceMonthly),
        priceYearly: parseFloat(planForm.priceYearly),
        pricePerUserMonthly: parseFloat(planForm.pricePerUserMonthly) || 0,
        pricePerUserYearly: parseFloat(planForm.pricePerUserYearly) || 0,
        minUsers: parseInt(planForm.minUsers) || 1,
        limits: {
          maxUsers: parseInt(planForm.maxUsers) || 0,
          maxClients: parseInt(planForm.maxClients) || 0,
          maxStorageGB: parseInt(planForm.maxStorageGB) || 0,
          maxEmailAccounts: parseInt(planForm.maxEmailAccounts) || 0,
          aiCredits: parseInt(planForm.aiCredits) || 0
        }
      };

      if (editingPlan) {
        await adminApi.updatePlan(editingPlan.id, payload);
        toast.success('Plan updated successfully');
      } else {
        // Generate slug from name
        payload.slug = planForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        await adminApi.createPlan(payload);
        toast.success('Plan created successfully');
      }
      setShowPlanModal(false);
      setEditingPlan(null);
      fetchData();
    } catch (err) {
      toast.error(editingPlan ? 'Failed to update plan' : 'Failed to create plan');
      console.error(err);
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!confirm(`Are you sure you want to delete the "${plan.name}" plan?`)) return;
    try {
      await adminApi.deletePlan(plan.id);
      toast.success('Plan deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete plan');
      console.error(err);
    }
  };

  // --- Add-On Handlers ---
  const handleOpenAddOnModal = (addOn = null) => {
    if (addOn) {
      setEditingAddOn(addOn);
      setAddOnForm({
        name: addOn.name,
        slug: addOn.slug,
        description: addOn.description || '',
        category: addOn.category || 'module',
        priceMonthly: addOn.priceMonthly,
        priceYearly: addOn.priceYearly,
        pricePerUserMonthly: addOn.pricePerUserMonthly || '',
        pricePerUserYearly: addOn.pricePerUserYearly || '',
        isPerUser: addOn.isPerUser || false,
        transactionFeePercent: addOn.transactionFeePercent || '',
        icon: addOn.icon || 'Puzzle',
        features: Array.isArray(addOn.features) ? addOn.features.join('\n') : '',
        isActive: addOn.isActive
      });
    } else {
      setEditingAddOn(null);
      setAddOnForm({
        name: '',
        slug: '',
        description: '',
        category: 'module',
        priceMonthly: '',
        priceYearly: '',
        pricePerUserMonthly: '',
        pricePerUserYearly: '',
        isPerUser: false,
        transactionFeePercent: '',
        icon: 'Puzzle',
        features: '',
        isActive: true
      });
    }
    setShowAddOnModal(true);
  };

  const handleSubmitAddOn = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...addOnForm,
        priceMonthly: parseFloat(addOnForm.priceMonthly) || 0,
        priceYearly: parseFloat(addOnForm.priceYearly) || 0,
        pricePerUserMonthly: parseFloat(addOnForm.pricePerUserMonthly) || 0,
        pricePerUserYearly: parseFloat(addOnForm.pricePerUserYearly) || 0,
        transactionFeePercent: parseFloat(addOnForm.transactionFeePercent) || 0,
        features: addOnForm.features.split('\n').map(f => f.trim()).filter(Boolean)
      };

      if (!editingAddOn) {
        payload.slug = addOnForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }

      if (editingAddOn) {
        await adminApi.updateAddOn(editingAddOn.id, payload);
        toast.success('Add-on updated successfully');
      } else {
        await adminApi.createAddOn(payload);
        toast.success('Add-on created successfully');
      }
      setShowAddOnModal(false);
      setEditingAddOn(null);
      fetchData();
    } catch (err) {
      toast.error(editingAddOn ? 'Failed to update add-on' : 'Failed to create add-on');
      console.error(err);
    }
  };

  const handleDeleteAddOn = async (addOn) => {
    if (!confirm(`Are you sure you want to deactivate "${addOn.name}"?`)) return;
    try {
      await adminApi.deleteAddOn(addOn.id);
      toast.success('Add-on deactivated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate add-on');
      console.error(err);
    }
  };

  // Helper to render feature objects or arrays
  const renderFeatures = (features) => {
    if (!features) return null;
    if (Array.isArray(features)) {
      return features;
    }
    // features is an object like { emailSync: true, ... }
    return Object.entries(features)
      .filter(([, v]) => v === true)
      .map(([key]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()));
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Puzzle;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans & Add-ons</h1>
          <p className="text-gray-500 mt-1">Manage subscription plans, integration modules, and add-ons</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'plans' ? (
            <button onClick={() => handleOpenPlanModal()} className="btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              New Plan
            </button>
          ) : (
            <button onClick={() => handleOpenAddOnModal()} className="btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              New Add-on
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('plans')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'plans'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Subscription Plans ({plans.length})
          </button>
          <button
            onClick={() => setActiveTab('addons')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'addons'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Puzzle className="w-4 h-4 inline mr-2" />
            Add-ons & Modules ({addOns.length})
          </button>
        </nav>
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {plans.map((plan) => {
            const features = renderFeatures(plan.features);
            return (
              <div key={plan.id} className={`card p-6 relative ${plan.isFeatured ? 'ring-2 ring-primary-500' : ''}`}>
                {plan.isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg mr-3">
                      <Zap className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                      <span className={plan.isActive ? 'badge-success' : 'badge-gray'}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenPlanModal(plan)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-4">{plan.description}</p>

                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-baseline mb-1">
                    <span className="text-3xl font-bold text-gray-900">${plan.priceMonthly}</span>
                    <span className="text-gray-500 ml-1">/month</span>
                  </div>
                  {plan.minUsers > 1 && (
                    <p className="text-xs text-gray-500 mb-1">
                      Includes {plan.minUsers} users
                    </p>
                  )}
                  {parseFloat(plan.pricePerUserMonthly) > 0 && (
                    <p className="text-sm font-medium text-primary-600">
                      + ${plan.pricePerUserMonthly}/user/month after {plan.minUsers} users
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">${plan.priceYearly}/year (save 2 months)</p>
                </div>

                <div className="border-t border-gray-100 mt-4 pt-4 space-y-3">
                  <div className="flex items-center text-sm">
                    <Users className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">
                      {plan.minUsers > 1 ? `${plan.minUsers} users included` : ''}{plan.limits?.maxUsers === -1 ? (plan.minUsers > 1 ? ', unlimited max' : 'Unlimited users') : plan.limits?.maxUsers > 0 ? (plan.minUsers > 1 ? `, up to ${plan.limits.maxUsers}` : `Up to ${plan.limits.maxUsers} users`) : 'Unlimited users'}
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">
                      {plan.limits?.maxClients === -1 ? 'Unlimited clients' : plan.limits?.maxClients > 0 ? `Up to ${plan.limits.maxClients} clients` : 'Unlimited clients'}
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <HardDrive className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">
                      {plan.limits?.maxStorageGB === -1 ? 'Unlimited storage' : plan.limits?.maxStorageGB > 0 ? `${plan.limits.maxStorageGB} GB storage` : 'Unlimited storage'}
                    </span>
                  </div>
                  {plan.limits?.aiCredits !== undefined && plan.limits?.aiCredits !== 0 && (
                    <div className="flex items-center text-sm">
                      <Zap className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        {plan.limits.aiCredits === -1 ? 'Unlimited AI credits' : `${plan.limits.aiCredits} AI credits`}
                      </span>
                    </div>
                  )}
                </div>

                {features && features.length > 0 && (
                  <div className="border-t border-gray-100 mt-4 pt-4">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2">Features</p>
                    <ul className="space-y-2">
                      {features.slice(0, 6).map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                      {features.length > 6 && (
                        <li className="text-sm text-gray-400">
                          +{features.length - 6} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="border-t border-gray-100 mt-4 pt-4">
                  <p className="text-xs text-gray-400">
                    {plan.subscriberCount || 0} active subscribers
                  </p>
                </div>
              </div>
            );
          })}

          {plans.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              No plans found. Create your first plan to get started.
            </div>
          )}
        </div>
      )}

      {/* Add-ons Tab */}
      {activeTab === 'addons' && (
        <div className="space-y-6">
          {/* Category Groups */}
          {['integration', 'module', 'feature'].map(category => {
            const categoryAddOns = addOns.filter(a => a.category === category);
            if (categoryAddOns.length === 0) return null;

            return (
              <div key={category}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {category === 'integration' ? 'Integrations' : category === 'module' ? 'Modules' : 'Features'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryAddOns.map(addOn => {
                    const IconComp = getIcon(addOn.icon);
                    return (
                      <div key={addOn.id} className="card p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <div className="p-2 bg-primary-100 rounded-lg mr-3">
                              <IconComp className="w-5 h-5 text-primary-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{addOn.name}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColors[addOn.category]}`}>
                                  {categoryLabels[addOn.category]}
                                </span>
                                <span className={addOn.isActive ? 'badge-success' : 'badge-gray'}>
                                  {addOn.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleOpenAddOnModal(addOn)}
                              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAddOn(addOn)}
                              className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">{addOn.description}</p>

                        <div className="border-t border-gray-100 pt-3">
                          <div className="flex items-baseline mb-1">
                            <span className="text-2xl font-bold text-gray-900">${addOn.priceMonthly}</span>
                            <span className="text-gray-500 ml-1">
                              {addOn.isPerUser ? '/user/month' : '/month'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">${addOn.priceYearly}/year</p>
                          {parseFloat(addOn.transactionFeePercent) > 0 && (
                            <p className="text-sm font-medium text-orange-600 mt-1">
                              + {addOn.transactionFeePercent}% transaction fee
                            </p>
                          )}
                        </div>

                        {Array.isArray(addOn.features) && addOn.features.length > 0 && (
                          <div className="border-t border-gray-100 mt-3 pt-3">
                            <ul className="space-y-1.5">
                              {addOn.features.slice(0, 4).map((feature, idx) => (
                                <li key={idx} className="flex items-center text-sm text-gray-600">
                                  <Check className="w-3.5 h-3.5 text-green-500 mr-2 flex-shrink-0" />
                                  {feature}
                                </li>
                              ))}
                              {addOn.features.length > 4 && (
                                <li className="text-xs text-gray-400">
                                  +{addOn.features.length - 4} more
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {addOns.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No add-ons found. Create your first add-on to get started.
            </div>
          )}
        </div>
      )}

      {/* Plan Create/Edit Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </h3>
              <button onClick={() => { setShowPlanModal(false); setEditingPlan(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitPlan} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>

              <p className="text-xs font-medium text-gray-500 uppercase">Base Pricing</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Monthly ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={planForm.priceMonthly}
                    onChange={(e) => setPlanForm({ ...planForm, priceMonthly: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Yearly ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={planForm.priceYearly}
                    onChange={(e) => setPlanForm({ ...planForm, priceYearly: e.target.value })}
                    className="input"
                    required
                  />
                </div>
              </div>

              <p className="text-xs font-medium text-gray-500 uppercase">Per-User Pricing</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Included Users</label>
                  <input
                    type="number"
                    value={planForm.minUsers}
                    onChange={(e) => setPlanForm({ ...planForm, minUsers: e.target.value })}
                    className="input"
                    placeholder="5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">$/user/month</label>
                  <input
                    type="number"
                    step="0.01"
                    value={planForm.pricePerUserMonthly}
                    onChange={(e) => setPlanForm({ ...planForm, pricePerUserMonthly: e.target.value })}
                    className="input"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">$/user/year</label>
                  <input
                    type="number"
                    step="0.01"
                    value={planForm.pricePerUserYearly}
                    onChange={(e) => setPlanForm({ ...planForm, pricePerUserYearly: e.target.value })}
                    className="input"
                    placeholder="0"
                  />
                </div>
              </div>

              <p className="text-xs font-medium text-gray-500 uppercase">Limits</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
                  <input
                    type="number"
                    value={planForm.maxUsers}
                    onChange={(e) => setPlanForm({ ...planForm, maxUsers: e.target.value })}
                    className="input"
                    placeholder="-1 = unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Clients</label>
                  <input
                    type="number"
                    value={planForm.maxClients}
                    onChange={(e) => setPlanForm({ ...planForm, maxClients: e.target.value })}
                    className="input"
                    placeholder="-1 = unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Storage (GB)</label>
                  <input
                    type="number"
                    value={planForm.maxStorageGB}
                    onChange={(e) => setPlanForm({ ...planForm, maxStorageGB: e.target.value })}
                    className="input"
                    placeholder="-1 = unlimited"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Accounts</label>
                  <input
                    type="number"
                    value={planForm.maxEmailAccounts}
                    onChange={(e) => setPlanForm({ ...planForm, maxEmailAccounts: e.target.value })}
                    className="input"
                    placeholder="-1 = unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AI Credits</label>
                  <input
                    type="number"
                    value={planForm.aiCredits}
                    onChange={(e) => setPlanForm({ ...planForm, aiCredits: e.target.value })}
                    className="input"
                    placeholder="-1 = unlimited"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={planForm.isActive}
                    onChange={(e) => setPlanForm({ ...planForm, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={planForm.isFeatured}
                    onChange={(e) => setPlanForm({ ...planForm, isFeatured: e.target.checked })}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Featured (Most Popular)</span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => { setShowPlanModal(false); setEditingPlan(null); }} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add-On Create/Edit Modal */}
      {showAddOnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingAddOn ? 'Edit Add-on' : 'Create New Add-on'}
              </h3>
              <button onClick={() => { setShowAddOnModal(false); setEditingAddOn(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitAddOn} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={addOnForm.name}
                  onChange={(e) => setAddOnForm({ ...addOnForm, name: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={addOnForm.description}
                  onChange={(e) => setAddOnForm({ ...addOnForm, description: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={addOnForm.category}
                    onChange={(e) => setAddOnForm({ ...addOnForm, category: e.target.value })}
                    className="input"
                  >
                    <option value="module">Module</option>
                    <option value="integration">Integration</option>
                    <option value="feature">Feature</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <select
                    value={addOnForm.icon}
                    onChange={(e) => setAddOnForm({ ...addOnForm, icon: e.target.value })}
                    className="input"
                  >
                    <option value="Puzzle">Puzzle</option>
                    <option value="MessageSquare">Message Square</option>
                    <option value="RefreshCw">Refresh/Sync</option>
                    <option value="DollarSign">Dollar Sign</option>
                    <option value="Zap">Lightning Bolt</option>
                    <option value="Shield">Shield</option>
                    <option value="BarChart3">Bar Chart</option>
                    <option value="FolderOpen">Folder</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={addOnForm.priceMonthly}
                    onChange={(e) => setAddOnForm({ ...addOnForm, priceMonthly: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={addOnForm.priceYearly}
                    onChange={(e) => setAddOnForm({ ...addOnForm, priceYearly: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Fee %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={addOnForm.transactionFeePercent}
                    onChange={(e) => setAddOnForm({ ...addOnForm, transactionFeePercent: e.target.value })}
                    className="input"
                    placeholder="0"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addOnForm.isPerUser}
                      onChange={(e) => setAddOnForm({ ...addOnForm, isPerUser: e.target.checked })}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Per-user pricing</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
                <textarea
                  value={addOnForm.features}
                  onChange={(e) => setAddOnForm({ ...addOnForm, features: e.target.value })}
                  className="input"
                  rows={5}
                  placeholder="Client self-service portal&#10;Digital engagement letters&#10;E-signatures"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="addOnActive"
                  checked={addOnForm.isActive}
                  onChange={(e) => setAddOnForm({ ...addOnForm, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <label htmlFor="addOnActive" className="ml-2 text-sm text-gray-700">
                  Active and available for tenants
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => { setShowAddOnModal(false); setEditingAddOn(null); }} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingAddOn ? 'Update Add-on' : 'Create Add-on'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansPage;
